import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { complianceService } from '../../services';
import { Modal, Select, Button, PageHeader, DocumentViewer } from '../../components';
import { ComplianceSkeleton } from '../../skeletons';
import { toast } from 'react-hot-toast';
import {
  ShieldCheck, Clock, AlertTriangle, FileUp, Plus, Info, ChevronUp, Download,
} from 'lucide-react';

/**
 * Staff Compliance — track Right to Work + DBS checks, evidence and renewal
 * reminders for every staff member. Managers record/update a check (with proof)
 * via the modal; the board classifies each check by expiry tier + proof.
 * Backed by /api/carehome-manager/compliance.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const REMINDER_OPTIONS = [
  { value: '90,60,30,7', label: '90, 60, 30 and 7 days' },
  { value: '60,30,7', label: '60, 30 and 7 days' },
  { value: '30,7', label: '30 and 7 days' },
  { value: '7', label: '7 days only' },
];

const STATUS_OPTIONS = [
  { value: 'valid', label: 'Valid' },
  { value: 'due_soon', label: 'Due soon' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
];

const fmt = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Expiry-tier / proof colour system (matches the agreed legend).
const STATE_META = {
  valid:     { label: 'VALID',        cls: 'bg-green-100 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400' },   // >90 days
  no_expiry: { label: 'NO EXPIRY',    cls: 'bg-green-100 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400' },   // genuinely no expiry
  due_soon:  { label: 'DUE SOON',     cls: 'bg-amber-100 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400' },   // 31–90 days
  urgent:    { label: 'URGENT',       cls: 'bg-orange-100 dark:bg-orange-900/10 text-orange-700 dark:text-orange-400' }, // 1–30 days
  overdue:   { label: 'OVERDUE',      cls: 'bg-rose-100 dark:bg-red-900/10 text-rose-700 dark:text-red-400' },     // passed
  missing:   { label: 'NOT RECORDED', cls: 'bg-slate-100 dark:bg-[#31373f] text-slate-500 dark:text-gray-400' },
};

// "(in N days)" / "(N days ago)" next to the expiry date.
const daysLabel = (c) => {
  if (!c.present || c.dueInDays == null) return '';
  if (c.dueInDays < 0) return `(${Math.abs(c.dueInDays)} days ago)`;
  if (c.dueInDays === 0) return '(today)';
  return `(in ${c.dueInDays} days)`;
};

const emptyForm = {
  user_id: '', check_type: 'rtw', document_type: '', status: 'valid',
  check_date: '', expiry_date: '', reference_no: '', reminder_schedule: '90,60,30,7',
  manager_notes: '', proofFile: null,
};

// ── Summary tile ─────────────────────────────────────────────────────────────
const Tile = ({ label, value, hint, icon: Icon, accent, iconBg }) => (
  <div
    className="group relative bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-5 transition-all hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_10px_24px_-6px_rgba(15,23,42,0.12)]"
    style={{ borderBottomColor: accent, borderBottomWidth: '3px' }}
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{label}</div>
        <div className="text-[34px] leading-none font-extrabold text-[#0B1220] dark:text-gray-100 mt-3 tabular-nums">{value}</div>
        <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">{hint}</div>
      </div>
      <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${iconBg}`}><Icon className="w-[22px] h-[22px]" /></span>
    </div>
  </div>
);

// ── One check card (RTW or DBS) ──────────────────────────────────────────────
const CheckCard = ({ title, check, onUpdate, onViewProof }) => {
  const badge = STATE_META[check.state] || STATE_META.missing;
  const dateLabel = title === 'DBS' ? 'Recheck' : 'Expiry';
  return (
    <div className="flex-1 min-w-[240px] border border-[#EEF2F8] dark:border-gray-800 rounded-2xl p-4 bg-[#FBFCFE] dark:bg-[#20242c] hover:border-[#E3E9F3] hover:bg-white dark:hover:bg-[#262b33] transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-wider text-[#94A3B8] dark:text-gray-500 uppercase">{title}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
      </div>
      {check.present ? (
        <>
          <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 mt-2">{check.document_type || '—'}</div>
          <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1 space-y-0.5">
            {check.check_date && <div>Checked: {fmt(check.check_date)}</div>}
            <div>
              {check.expiry_date ? `${dateLabel}: ${fmt(check.expiry_date)} ` : 'Expiry: No expiry'}
              {check.expiry_date && <span className={check.state === 'overdue' ? 'text-rose-600 font-semibold' : check.state === 'urgent' ? 'text-orange-600 font-semibold' : 'text-[#94A3B8]'}>{daysLabel(check)}</span>}
            </div>
          </div>
          <div className={`mt-3 flex items-center justify-between rounded-lg px-3 py-2 text-[12px] font-semibold ${check.proof_url ? 'bg-green-50 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400' : 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'}`}>
            <span>Evidence</span>
            {check.proof_url ? (
              <button type="button" onClick={onViewProof} className="underline hover:no-underline">Uploaded</button>
            ) : (<span>Missing</span>)}
          </div>
        </>
      ) : (
        <div className="text-[13px] text-[#94A3B8] dark:text-gray-500 mt-3 mb-2">No {title} check recorded yet.</div>
      )}
      <button onClick={onUpdate} className="mt-3 w-full text-[12px] font-semibold text-[#2563EB] dark:text-blue-400 border border-[#BFDBFE] dark:border-blue-900/50 rounded-lg py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
        {check.present ? 'Update' : 'Add check'}
      </button>
    </div>
  );
};

const StaffCompliance = () => {
  const [board, setBoard] = useState([]);
  const [summary, setSummary] = useState({ clear: 0, dueIn90: 0, overdue: 0, proofMissing: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guideOpen, setGuideOpen] = useState(true);

  // Filters + sort
  const [staffFilter, setStaffFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('both');
  const [statusFilter, setStatusFilter] = useState('');
  const [proofFilter, setProofFilter] = useState('');
  const [sortBy, setSortBy] = useState('');            // '' | rtw_asc | rtw_desc | dbs_asc | dbs_desc
  const [noExpiryBottom, setNoExpiryBottom] = useState(true);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [proof, setProof] = useState(null);   // { url, name, title } — evidence preview
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchBoard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await complianceService.list();
      if (res.success) {
        setBoard(res.data || []);
        setSummary(res.summary || { clear: 0, dueIn90: 0, overdue: 0, proofMissing: 0 });
      } else {
        setError(res.message || 'Failed to load compliance');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading compliance');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setForm(emptyForm); setModalOpen(true); };

  const openEdit = (member, checkType) => {
    const c = member[checkType] || {};
    setForm({
      user_id: String(member.id), check_type: checkType,
      document_type: c.document_type || '', status: c.status || 'valid',
      check_date: c.check_date ? String(c.check_date).slice(0, 10) : '',
      expiry_date: c.expiry_date ? String(c.expiry_date).slice(0, 10) : '',
      reference_no: c.reference_no || '', reminder_schedule: c.reminder_schedule || '90,60,30,7',
      manager_notes: c.manager_notes || '', proofFile: null,
    });
    setModalOpen(true);
  };

  // Evidence is previewed in-page (modal) rather than opened in a new tab.
  const openProof = (member, check, label) => {
    if (!check?.proof_url) return;
    const url = `${API_ORIGIN}${check.proof_url}`;
    setProof({
      url,
      name: decodeURIComponent(String(check.proof_url).split('/').pop() || ''),
      title: `${member.name} — ${label} evidence`,
    });
  };

  const handleSave = async () => {
    if (!form.user_id) { toast.error('Please select a staff member'); return; }
    if (!['rtw', 'dbs'].includes(form.check_type)) { toast.error('Please choose a check type'); return; }
    try {
      setSaving(true);
      const fd = new FormData();
      ['user_id', 'check_type', 'document_type', 'status', 'check_date', 'expiry_date', 'reference_no', 'reminder_schedule', 'manager_notes']
        .forEach((k) => fd.append(k, form[k]));
      if (form.proofFile) fd.append('proof', form.proofFile);
      const res = await complianceService.save(fd);
      if (res.success) { toast.success('Compliance check saved'); setModalOpen(false); fetchBoard(); }
      else toast.error(res.message || 'Failed to save');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Client-side filter.
  const filtered = useMemo(() => board.filter((m) => {
    if (staffFilter && String(m.id) !== String(staffFilter)) return false;
    const checks = [];
    if (typeFilter !== 'dbs') checks.push(m.rtw);
    if (typeFilter !== 'rtw') checks.push(m.dbs);
    const present = checks.filter((c) => c && c.present);
    if (statusFilter && !present.some((c) => c.state === statusFilter)) return false;
    if (proofFilter === 'uploaded' && !present.some((c) => c.proof_url)) return false;
    if (proofFilter === 'missing' && !present.some((c) => c.proofMissing)) return false;
    return true;
  }), [board, staffFilter, typeFilter, statusFilter, proofFilter]);

  // Sort by expiry (of the chosen check type). No-expiry / missing pushed per toggle.
  const visible = useMemo(() => {
    if (!sortBy) return filtered;
    const [type, dir] = sortBy.split('_');
    const val = (m) => {
      const c = type === 'dbs' ? m.dbs : m.rtw;
      return (c && c.present && c.dueInDays != null) ? c.dueInDays : null;
    };
    return [...filtered].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return noExpiryBottom ? 1 : -1;
      if (bv == null) return noExpiryBottom ? -1 : 1;
      return dir === 'asc' ? av - bv : bv - av;
    });
  }, [filtered, sortBy, noExpiryBottom]);

  const handleExport = () => {
    const header = ['Employee', 'Role', 'Check', 'Document', 'Status', 'Checked', 'Expiry/Recheck', 'Days left', 'Reference', 'Evidence', 'Last updated', 'Updated by'];
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [];
    visible.forEach((m) => {
      [['Right to Work', m.rtw], ['DBS', m.dbs]].forEach(([label, c]) => {
        if (!c.present) return;
        rows.push([
          m.name, m.role_label, label, c.document_type || '', (STATE_META[c.state]?.label || c.state),
          c.check_date || '', c.expiry_date || 'No expiry', c.dueInDays == null ? '' : c.dueInDays,
          c.reference_no || '', c.proof_url ? 'Uploaded' : 'Missing', c.updated_at ? fmt(c.updated_at) : '', c.updated_by || '',
        ].map(esc).join(','));
      });
    });
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `staff-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  if (loading && board.length === 0) return <ComplianceSkeleton />;

  return (
    <div className="compliance-page w-full space-y-5 pb-12 font-sans">
      {/* Header */}
      <PageHeader
        title="Staff Compliance"
        subtitle="Track Right to Work, DBS checks, proof and renewal reminders"
        actions={(
          <>
            <Button variant="secondary" leftIcon={<Info className="w-4 h-4 text-[#2563EB]" />} onClick={() => setGuideOpen((v) => !v)}>
              Guide
            </Button>
            <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />} onClick={handleExport}>
              Export
            </Button>
            <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Add check
            </Button>
          </>
        )}
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Clear" value={summary.clear} hint="RTW and DBS in date" icon={ShieldCheck} accent="#10B981" iconBg="bg-green-50 text-green-600" />
        <Tile label="Due in 90 days" value={summary.dueIn90} hint="Reminder cycle active" icon={Clock} accent="#F59E0B" iconBg="bg-amber-50 text-amber-600" />
        <Tile label="Overdue" value={summary.overdue} hint="Needs manager action" icon={AlertTriangle} accent="#F43F5E" iconBg="bg-rose-50 text-rose-600" />
        <Tile label="Proof missing" value={summary.proofMissing} hint="Upload evidence" icon={FileUp} accent="#8B5CF6" iconBg="bg-violet-50 text-violet-600" />
      </div>

      {/* Guide */}
      {guideOpen && (
        <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[14px] font-semibold text-[#1E293B] dark:text-gray-200"><Info className="w-4 h-4 text-[#2563EB]" /> Reminder and evidence guide</div>
            <button onClick={() => setGuideOpen(false)} className="text-[#94A3B8] dark:text-gray-500 hover:text-[#475569] dark:hover:text-gray-300"><ChevronUp className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { t: 'Reminder timing', d: 'Start at 90 days, then 60, 30, 7 days, due date and overdue.' },
              { t: 'Right to Work', d: 'Record check date, document/share-code evidence, expiry or no expiry.' },
              { t: 'DBS', d: 'Record certificate details, workforce/level, proof and the next recheck date.' },
            ].map((g) => (
              <div key={g.t} className="bg-[#F8FAFC] dark:bg-[#20242c] border border-[#EEF2F7] dark:border-gray-800 rounded-xl p-3">
                <div className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200">{g.t}</div>
                <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-1">{g.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + list */}
      <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]">
        <div className="p-4 border-b border-[#EEF2F8] dark:border-gray-800 flex flex-wrap items-center gap-3">
          <Select
            value={staffFilter}
            onChange={(v) => setStaffFilter(v)}
            options={[{ value: '', label: 'All staff' }, ...board.map((m) => ({ value: String(m.id), label: m.name }))]}
            className="w-[160px]"
          />
          <Select
            value={typeFilter}
            onChange={(v) => setTypeFilter(v)}
            options={[
              { value: 'both', label: 'DBS and RTW' },
              { value: 'dbs', label: 'DBS only' },
              { value: 'rtw', label: 'Right to Work only' },
            ]}
            className="w-[150px]"
          />
          <Select
            value={sortBy}
            onChange={(v) => setSortBy(v)}
            options={[
              { value: '', label: 'Sort by expiry' },
              { value: 'rtw_asc', label: 'Expiring soonest first' },
              { value: 'rtw_desc', label: 'Expiring latest first' },
              { value: 'dbs_asc', label: 'Recheck soonest first' },
              { value: 'dbs_desc', label: 'Recheck latest first' },
            ]}
            className="w-[200px]"
          />
          <label className="flex items-center gap-1.5 text-[12px] text-[#64748B] dark:text-gray-400 select-none">
            <input type="checkbox" checked={noExpiryBottom} onChange={(e) => setNoExpiryBottom(e.target.checked)} /> No expiry at bottom
          </label>
          <div className="flex-1" />
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'valid', label: 'Valid' },
              { value: 'due_soon', label: 'Due soon' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'overdue', label: 'Overdue' },
            ]}
            className="w-[150px]"
          />
          <Select
            value={proofFilter}
            onChange={(v) => setProofFilter(v)}
            options={[
              { value: '', label: 'All proof' },
              { value: 'uploaded', label: 'Proof uploaded' },
              { value: 'missing', label: 'Proof missing' },
            ]}
            className="w-[150px]"
          />
        </div>

        {error ? (
          <div className="p-12 text-center">
            <div className="bg-rose-50 dark:bg-red-900/10 border border-rose-100 dark:border-red-900/50 text-rose-600 dark:text-red-400 px-6 py-4 rounded-xl inline-block">{error}</div>
            <button onClick={fetchBoard} className="block mx-auto mt-4 text-sm text-blue-600 font-medium hover:underline">Try Again</button>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-20 text-center text-[14px] text-[#64748B] dark:text-gray-400">No staff match your filters.</div>
        ) : (
          <div className="divide-y divide-[#EEF2F7] dark:divide-gray-800">
            <div className="hidden lg:flex items-center px-6 py-3 bg-[#F8FAFC] dark:bg-[#20242c] text-[11px] font-bold uppercase tracking-wider text-[#64748B] dark:text-gray-400">
              <div className="w-[200px]">Employee</div>
              <div className="flex-1">Compliance checks</div>
              <div className="w-[180px] text-right">Actions</div>
            </div>
            {visible.map((m) => {
              const lastUpdated = [m.rtw, m.dbs]
                .filter((c) => c.present && c.updated_at)
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
              return (
                <div key={m.id} className="flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-5">
                  <div className="w-full lg:w-[200px] shrink-0">
                    <div className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">{m.name}</div>
                    <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">{m.role_label} · {m.code}</div>
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row gap-3">
                    <CheckCard title="Right to Work" check={m.rtw} onUpdate={() => openEdit(m, 'rtw')} onViewProof={() => openProof(m, m.rtw, 'Right to Work')} />
                    <CheckCard title="DBS" check={m.dbs} onUpdate={() => openEdit(m, 'dbs')} onViewProof={() => openProof(m, m.dbs, 'DBS')} />
                  </div>
                  <div className="w-full lg:w-[180px] flex flex-col gap-1.5 lg:items-end">
                    <Button variant="primary" size="sm" className="w-full lg:w-auto" onClick={() => openEdit(m, 'rtw')}>
                      Update details
                    </Button>
                    {lastUpdated && (
                      <div className="text-[11px] text-[#94A3B8] dark:text-gray-500 lg:text-right leading-tight">
                        Updated {fmt(lastUpdated.updated_at)}
                        {lastUpdated.updated_by ? <> by <span className="font-semibold text-[#64748B] dark:text-gray-400">{lastUpdated.updated_by}</span></> : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Update modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Update Compliance Check" maxWidth="max-w-[720px]">
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 -mt-2 mb-4">Record DBS or Right to Work evidence, due dates and reminders.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Staff member">
            <Select
              value={form.user_id}
              onChange={(v) => setField('user_id', v)}
              options={board.map((m) => ({ value: String(m.id), label: `${m.name} — ${m.role_label}` }))}
              placeholder="Select staff…"
              className="w-full"
            />
          </Field>
          <Field label="Check type">
            <Select
              value={form.check_type}
              onChange={(v) => setField('check_type', v)}
              options={[
                { value: 'rtw', label: 'Right to Work' },
                { value: 'dbs', label: 'DBS' },
              ]}
              className="w-full"
            />
          </Field>
          <Field label="Document type">
            <input value={form.document_type} onChange={(e) => setField('document_type', e.target.value)} placeholder={form.check_type === 'dbs' ? 'e.g. Enhanced adult workforce' : 'e.g. British passport / Share code'} className="cinp" />
          </Field>
          <Field label="Status">
            <Select
              value={form.status}
              onChange={(v) => setField('status', v)}
              options={STATUS_OPTIONS}
              className="w-full"
            />
          </Field>
          <Field label="Check / issue date">
            <input type="date" value={form.check_date} onChange={(e) => setField('check_date', e.target.value)} className="cinp" />
          </Field>
          <Field label="Expiry / recheck due">
            <input type="date" value={form.expiry_date} onChange={(e) => setField('expiry_date', e.target.value)} className="cinp" />
          </Field>
          <Field label="Reference / certificate no">
            <input value={form.reference_no} onChange={(e) => setField('reference_no', e.target.value)} placeholder="e.g. Passport copy / cert number" className="cinp" />
          </Field>
          <Field label="Reminder schedule">
            <Select
              value={form.reminder_schedule}
              onChange={(v) => setField('reminder_schedule', v)}
              options={REMINDER_OPTIONS}
              className="w-full"
            />
          </Field>
          <Field label="Proof attachment">
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setField('proofFile', e.target.files?.[0] || null)} className="text-[13px]" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Manager notes">
              <textarea rows={3} value={form.manager_notes} onChange={(e) => setField('manager_notes', e.target.value)} className="cinp resize-none" />
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-4 mt-2 border-t border-[#EEF2F8] dark:border-gray-800">
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>Save Compliance Check</Button>
        </div>
      </Modal>

      {/* Evidence opens in place. This viewer used to live here; it is now the
          shared one every screen uses, so a certificate looks the same whether
          it is opened from Compliance, a staff record or a candidate's form. */}
      <DocumentViewer file={proof} onClose={() => setProof(null)} />

      <style>{`
        .cinp{width:100%;padding:0.5rem 0.75rem;border:1px solid #E2E8F0;border-radius:0.6rem;font-size:13px;color:#0F172A;background:#fff;outline:none}
        .cinp:focus{border-color:#3B82F6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}
        .compliance-page select{
          -webkit-appearance:none;-moz-appearance:none;appearance:none;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat:no-repeat;background-position:right 0.65rem center;background-size:16px;padding-right:2rem;cursor:pointer;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">{label}</label>
    {children}
  </div>
);

export default StaffCompliance;
